// Vollea — Admin Users list with in-row role reassignment.
// Super_admin picks a new role from the dropdown → POST /admin/users/{id}/role
// → local state updates immediately (optimistic). Server safety rails cover
// self-change + last-super-admin — errors surface inline under the row.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoChevronBack } from "react-icons/io5";
import { Input, Avatar } from "../../components/ui-vollea";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { C, SP, R } from "../../lib/theme";

const ROLE_OPTIONS = [
  { value: "player", label: "Player" },
  { value: "club_admin", label: "Club Admin" },
  { value: "league_organiser", label: "League Organiser" },
  { value: "super_admin", label: "Super Admin" },
];

export default function AdminUsers() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [rowMsg, setRowMsg] = useState({}); // { [user_id]: {text, ok} }

  useEffect(() => {
    api.get("/admin/users").then((d) => setUsers(d || [])).catch(() => setUsers([]));
  }, []);

  const filtered = users.filter((u) => !q || `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));

  const changeRole = async (u, nextRole) => {
    if (nextRole === u.role) return;
    const label = ROLE_OPTIONS.find((r) => r.value === nextRole)?.label || nextRole;
    if (!window.confirm(`Change ${u.name || u.email}'s role to ${label}?`)) return;
    setSavingId(u.user_id);
    setRowMsg((m) => ({ ...m, [u.user_id]: null }));
    try {
      await api.post(`/admin/users/${u.user_id}/role`, { role: nextRole });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, role: nextRole } : x)));
      setRowMsg((m) => ({ ...m, [u.user_id]: { text: `Updated to ${label}`, ok: true } }));
    } catch (e) {
      setRowMsg((m) => ({ ...m, [u.user_id]: { text: e.message || "Update failed", ok: false } }));
    } finally {
      setSavingId(null);
      setTimeout(() => setRowMsg((m) => ({ ...m, [u.user_id]: null })), 4000);
    }
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: `${SP.lg}px ${SP.lg}px 90px` }}>
      <button onClick={() => navigate("/admin")} data-testid="users-back" style={{ color: C.text, marginBottom: SP.md, background: "transparent", border: "none", cursor: "pointer" }}>
        <IoChevronBack size={26} />
      </button>
      <h1 style={{ color: C.text, fontWeight: 700, fontSize: 28, margin: 0, letterSpacing: 0.5 }}>Users</h1>
      <p style={{ color: C.textMuted, fontSize: 14, marginTop: 4 }}>{users.length} total</p>

      <div style={{ marginTop: SP.xl }}>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" testid="users-search-input" />
      </div>

      <div style={{ marginTop: SP.md, display: "flex", flexDirection: "column", gap: SP.sm }} data-testid="users-list">
        {filtered.length === 0 ? (
          <div style={{ color: C.textMuted, fontSize: 13, padding: SP.lg, textAlign: "center" }}>No users.</div>
        ) : filtered.map((u) => {
          const isSelf = me?.user_id === u.user_id;
          const busy = savingId === u.user_id;
          const msg = rowMsg[u.user_id];
          return (
            <div key={u.user_id} data-testid={`user-row-${u.user_id}`} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.md, padding: SP.md, display: "flex", flexDirection: "column", gap: SP.sm }}>
              <div style={{ display: "flex", alignItems: "center", gap: SP.md }}>
                <Avatar uri={u.photo} name={u.name} size={40} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: C.text, fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
                    {u.name}
                    {isSelf ? (
                      <span style={{ color: C.textDim, fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>· you</span>
                    ) : null}
                  </div>
                  <div style={{ color: C.textMuted, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</div>
                </div>
                <select
                  value={u.role || "player"}
                  onChange={(e) => changeRole(u, e.target.value)}
                  disabled={isSelf || busy}
                  data-testid={`user-role-select-${u.user_id}`}
                  title={isSelf ? "You can't change your own role" : "Change this user's role"}
                  style={{
                    background: C.surface2, border: `1px solid ${C.borderStrong}`, borderRadius: R.sm,
                    color: isSelf ? C.textDim : C.lime, fontWeight: 700, fontSize: 12,
                    letterSpacing: 0.5, padding: `6px ${SP.sm}px`, height: 32,
                    cursor: isSelf ? "not-allowed" : "pointer",
                    textTransform: "uppercase",
                  }}
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value} style={{ background: C.surface, color: C.text, textTransform: "none" }}>{r.label}</option>
                  ))}
                </select>
              </div>
              {msg ? (
                <div
                  data-testid={`user-role-msg-${u.user_id}`}
                  style={{
                    fontSize: 12, fontWeight: 600, padding: `6px ${SP.md}px`, borderRadius: R.sm,
                    background: msg.ok ? C.limeTint : "rgba(239,68,68,0.1)",
                    color: msg.ok ? C.lime : C.error,
                    border: `1px solid ${msg.ok ? C.lime : C.error}`,
                  }}
                >{msg.text}</div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
